import { useState } from "react"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { GetRequest, PutRequest } from "../../services/ApiClientService"
import { useLoad } from "../../context/useLoadContext"
import { useError } from "../../context/useErrorContext"
import { useTranslation } from "react-i18next"
import { OrderPaymentProvider } from "../../Objects/Payment/OrderPaymentProvider"
import { EmployeeTapToPayDevice } from "../../Objects/Payment/EmployeeTapToPayDevice"
import { MercadoPagoPointDevice } from "../../Objects/Payment/MercadoPagoPointDevice"
import { EOrderPaymentProviderType } from "../../Enum/EOrderPaymentProviderType"

export const useOrderPaymentProviders = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const [providers, setProviders] = useState<OrderPaymentProvider[]>([])

  const [devices, setDevices] = useState<EmployeeTapToPayDevice[]>([])

  const [mercadoPagoPointDevices, setMercadoPagoPointDevices] = useState<MercadoPagoPointDevice[]>([])

  const safeRequest = async (callback: any) => {
    enableLoad()

    try {
      return await callback()
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)

      return null
    } finally {
      disableLoad()
    }
  }

  const handleGetProviders = async () =>
    safeRequest(async () => {
      const response = await GetRequest<OrderPaymentProvider[]>(API_ROUTES.ORDER_PAYMENT_PROVIDER.LIST)

      if (response.success) {
        setProviders((response.data ?? []).map((p) => new OrderPaymentProvider(p)))
      }

      return response
    })

  const handleUpdateProviderCapabilities = async (payload: {
    providerType: EOrderPaymentProviderType
    isEnabled: boolean
    isOnlinePaymentEnabled: boolean
    isInPersonPaymentEnabled: boolean
    isTapToPayEnabled: boolean
  }) =>
    safeRequest(async () => {
      const response = await PutRequest(API_ROUTES.ORDER_PAYMENT_PROVIDER.UPDATE_CAPABILITIES, payload)

      if (response.success) {
        await handleGetProviders()
      }

      return response
    })

  const handleGetTapToPayDevices = async () =>
    safeRequest(async () => {
      const response = await GetRequest<EmployeeTapToPayDevice[]>(
        API_ROUTES.ORDER_PAYMENT_PROVIDER.TAP_TO_PAY_DEVICES_LIST,
      )

      if (response.success) {
        setDevices((response.data ?? []).map((d) => new EmployeeTapToPayDevice(d)))
      }

      return response
    })

  const handleUpsertTapToPayDevice = async (payload: {
    employeeEntityId: string
    providerType: EOrderPaymentProviderType
    deviceId: string
    label?: string
  }) =>
    safeRequest(async () => {
      const response = await PutRequest(API_ROUTES.ORDER_PAYMENT_PROVIDER.TAP_TO_PAY_DEVICES_UPSERT, payload)

      if (response.success) {
        await handleGetTapToPayDevices()
      }

      return response
    })

  const handleGetMercadoPagoPointDevices = async () =>
    safeRequest(async () => {
      const response = await GetRequest<MercadoPagoPointDevice[]>(API_ROUTES.ORDER_PAYMENT_PROVIDER.MERCADOPAGO_POINT_DEVICES)

      if (response.success) {
        setMercadoPagoPointDevices((response.data ?? []).map((d) => new MercadoPagoPointDevice(d)))
      }

      return response
    })

  return {
    providers,
    devices,
    mercadoPagoPointDevices,
    handleGetProviders,
    handleUpdateProviderCapabilities,
    handleGetTapToPayDevices,
    handleUpsertTapToPayDevice,
    handleGetMercadoPagoPointDevices,
  }
}
